import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AccountContext = createContext(null);

const STORAGE_KEY = "veerox_active_account_id";

export const AccountProvider = ({ children }) => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [activeAccountId, setActiveAccountIdState] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!getToken()) {
      setAccounts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/accounts");
      const list = r.data?.accounts || [];
      setAccounts(list);
      // Ensure the persisted active is still valid; else auto-select the best one.
      const stored = localStorage.getItem(STORAGE_KEY);
      const valid = list.find((a) => a.id === stored);
      if (!valid) {
        // Auto-select priority: (1) most-recently-handshook non-provisional account
        // (i.e. the one just claimed via /connect), (2) is_default, (3) first non-provisional,
        // (4) first of any kind. This makes the CEO land on the account they just claimed.
        const withHb = list
          .filter((a) => !a.provisional && a.last_handshake_at)
          .sort((a, b) =>
            String(b.last_handshake_at || "").localeCompare(String(a.last_handshake_at || ""))
          );
        const nonProv = list.filter((a) => !a.provisional);
        const def =
          withHb[0] ||
          list.find((a) => a.is_default) ||
          nonProv[0] ||
          list[0];
        if (def) {
          setActiveAccountIdState(def.id);
          localStorage.setItem(STORAGE_KEY, def.id);
        }
      } else if (activeAccountId !== stored) {
        setActiveAccountIdState(stored);
      }
    } catch (e) {
      // silent — user might not be logged in
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch whenever the authenticated user changes (login / register / logout).
  // user changing from null → object triggers the fetch that would otherwise
  // be missed on the initial provider mount.
  useEffect(() => {
    if (user?.id) {
      load();
    } else {
      setAccounts([]);
      localStorage.removeItem(STORAGE_KEY);
      setActiveAccountIdState("");
      setLoading(false);
    }
  }, [user?.id, load]);

  const setActiveAccountId = useCallback((id) => {
    setActiveAccountIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const activeAccount = useMemo(
    () => accounts.find((a) => a.id === activeAccountId) || accounts.find((a) => a.is_default) || accounts[0] || null,
    [accounts, activeAccountId],
  );

  const value = useMemo(() => ({
    accounts, activeAccount, activeAccountId: activeAccount?.id || "",
    setActiveAccountId, refresh: load, loading,
  }), [accounts, activeAccount, setActiveAccountId, load, loading]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccounts = () => {
  const ctx = useContext(AccountContext);
  if (!ctx) return { accounts: [], activeAccount: null, activeAccountId: "", setActiveAccountId: () => {}, refresh: () => {}, loading: false };
  return ctx;
};

/** Convenience helper: append ?account_id= to a path if we have one. */
export const withAccountParam = (path, accountId) => {
  if (!accountId) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}account_id=${encodeURIComponent(accountId)}`;
};
